import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Contract } from '../types/contract';
import { evolutionWhatsAppService } from './evolutionWhatsAppService';
import { notificationService } from './notificationService';

export interface WhatsAppNotificationSettings {
  enabled: boolean;
  destinationNumbers: string[];
  instanceName: string;
}

export interface WhatsAppNotificationSendResult {
  number: string;
  success: boolean;
  error?: string;
}

const SETTINGS_COLLECTION = 'app_settings';
const SETTINGS_DOC = 'whatsapp_notifications';

export const DEFAULT_WHATSAPP_NOTIFICATION_SETTINGS: WhatsAppNotificationSettings = {
  enabled: false,
  destinationNumbers: [],
  instanceName: 'playkids'
};

const cleanNumber = (value: string) => value.replace(/\D/g, '');

export const whatsappNotificationSettingsService = {
  async getSettings(): Promise<WhatsAppNotificationSettings> {
    try {
      const snapshot = await getDoc(doc(db, SETTINGS_COLLECTION, SETTINGS_DOC));
      if (!snapshot.exists()) return DEFAULT_WHATSAPP_NOTIFICATION_SETTINGS;

      const data = snapshot.data() as Partial<WhatsAppNotificationSettings> & { destinationNumber?: string };

      // Compatibilidade com configuracoes salvas antes do suporte a
      // multiplos numeros (campo unico "destinationNumber").
      const legacyNumber = data.destinationNumber ? [cleanNumber(data.destinationNumber)] : [];
      const numbers = Array.isArray(data.destinationNumbers) && data.destinationNumbers.length > 0
        ? data.destinationNumbers.map(cleanNumber)
        : legacyNumber;

      return {
        enabled: Boolean(data.enabled),
        destinationNumbers: numbers.filter(Boolean),
        instanceName: data.instanceName || DEFAULT_WHATSAPP_NOTIFICATION_SETTINGS.instanceName
      };
    } catch (error) {
      console.error('Erro ao carregar configuracoes de WhatsApp:', error);
      return DEFAULT_WHATSAPP_NOTIFICATION_SETTINGS;
    }
  },

  async saveSettings(settings: WhatsAppNotificationSettings): Promise<void> {
    await setDoc(
      doc(db, SETTINGS_COLLECTION, SETTINGS_DOC),
      {
        enabled: Boolean(settings.enabled),
        destinationNumbers: settings.destinationNumbers.map(cleanNumber).filter(Boolean),
        instanceName: settings.instanceName || DEFAULT_WHATSAPP_NOTIFICATION_SETTINGS.instanceName,
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );
  },

  async sendContractNotificationIfEnabled(
    contract: Contract
  ): Promise<{ sent: boolean; reason?: string; results?: WhatsAppNotificationSendResult[] }> {
    const settings = await this.getSettings();

    if (!settings.enabled) {
      return { sent: false, reason: 'Envio automatico de WhatsApp desativado.' };
    }

    if (settings.destinationNumbers.length === 0) {
      return { sent: false, reason: 'Nenhum numero de destino configurado.' };
    }

    const message = notificationService.buildNewContractMessage(contract);

    const results = await Promise.all(
      settings.destinationNumbers.map(async (number): Promise<WhatsAppNotificationSendResult> => {
        try {
          const result = await evolutionWhatsAppService.sendTestMessage(settings.instanceName, message, number);
          return { number: result.number, success: true };
        } catch (error) {
          return {
            number,
            success: false,
            error: error instanceof Error ? error.message : 'Erro desconhecido.'
          };
        }
      })
    );

    return { sent: results.some((r) => r.success), results };
  }
};
